import React, { useRef, useEffect, useState } from 'react';
import { RotateCcw, RotateCw, Loader2, Download, LayoutDashboard, FileText, Palette, Box, Coins } from 'lucide-react';

interface EditorProps {
  imageUrl: string;
  onProcess: (imageData: string, prompt: string, aspectRatio: string, restoreRect: {x: number, y: number, width: number, height: number} | null, is360?: boolean, creditCost?: number, referenceImageData?: string) => void;
  isProcessing: boolean;
  onDownload: () => void;
  onOpenDashboard: () => void;
  /** Optional user-uploaded floor plan whose style the AI should copy. Overrides the built-in reference. */
  styleReferenceDataUrl?: string;
}

// Reference floor plan image URL
const REFERENCE_FLOOR_PLAN_URL = '/reference-floorplan.png';

// Floor Plan Conversion Styles
const CONVERSION_STYLES = [
  {
    id: 'standard-2d',
    name: 'Standard 2D',
    description: 'Clean black & white lines. Professional & precise.',
    credits: 10,
    icon: FileText,
    color: 'teal',
    prompt: `TASK: Convert the hand-drawn floor plan sketch (IMAGE 1) into a professional 2D floor plan.
IMAGE 2 shows the EXACT output style to match.

⚠️ EXTREME CAUTION REQUIRED - READ CAREFULLY ⚠️

THIS IS A TRACING TASK, NOT A DESIGN TASK.
You must reproduce EXACTLY what is drawn in the sketch - nothing more, nothing less.
DO NOT use your imagination. DO NOT add anything that is not clearly visible.
If you are unsure about something, LEAVE IT OUT rather than guess.

MANDATORY ACCURACY RULES:
1. COUNT THE ROOMS: Count every distinct room/space in the sketch. Your output MUST have the IDENTICAL number of rooms.
2. TRACE WALLS EXACTLY: Follow the wall lines precisely as drawn. Do not straighten, extend, or modify them beyond basic cleanup.
3. PRESERVE PROPORTIONS: If a room looks small in the sketch, it should look small in the output. Do not resize or rebalance.
4. NO INVENTION: If you cannot clearly see a door, window, or opening - DO NOT ADD ONE.
5. NO HALLWAYS: Do not add corridors or passages unless they are explicitly drawn.
6. NO EXTRA ROOMS: Do not add closets, storage, en-suites, or any space not clearly shown.

WHAT TO INCLUDE (only if clearly visible in sketch):
- Walls exactly as drawn
- Doors/openings where clearly indicated
- Windows where clearly marked
- Room labels if written in the sketch
- Stairs if drawn

OUTPUT STYLE (match IMAGE 2):
- Pure white background
- Solid black walls
- Room labels: ALL CAPS, centered
- Dimensions: Imperial + metric (e.g., 11'5 x 8'4 / 3.5m x 2.5m)
- Door swings as quarter-circle arcs
- Standard architectural symbols

🚫 ABSOLUTELY FORBIDDEN:
- Adding rooms that don't exist in the sketch
- Adding closets, en-suites, or utility rooms not shown
- Adding doors/windows where none are indicated
- Changing the shape or size of any room
- "Improving" or "completing" the layout
- Adding furniture (this is Standard 2D - no furniture)
- Any branding or watermarks

BEFORE GENERATING: Count the rooms in the sketch. Your output MUST match this count exactly.`
  },
  {
    id: 'textured-2d',
    name: 'Textured 2D',
    description: 'With flooring textures and furniture icons.',
    credits: 10,
    icon: Palette,
    color: 'purple',
    prompt: `TASK: Convert the hand-drawn floor plan sketch (IMAGE 1) into a 2D floor plan with textures.
IMAGE 2 shows the layout style to follow.

⚠️ EXTREME CAUTION REQUIRED - READ CAREFULLY ⚠️

THIS IS A TRACING TASK, NOT A DESIGN TASK.
You must reproduce EXACTLY what is drawn in the sketch - nothing more, nothing less.
DO NOT use your imagination. DO NOT add anything that is not clearly visible.

MANDATORY ACCURACY RULES:
1. COUNT THE ROOMS: Count every distinct room/space in the sketch. Your output MUST have the IDENTICAL number of rooms.
2. TRACE WALLS EXACTLY: Follow the wall lines precisely as drawn.
3. PRESERVE PROPORTIONS: Do not resize or rebalance rooms.
4. NO INVENTION: If you cannot clearly see something - DO NOT ADD IT.
5. NO EXTRA ROOMS: Do not add closets, storage, en-suites, or any space not clearly shown.

WHAT TO INCLUDE (only if clearly visible in sketch):
- Walls exactly as drawn
- Doors/openings where clearly indicated
- Windows where clearly marked
- Room labels: ALL CAPS, centered

TEXTURES AND FURNITURE (only in rooms that EXIST in the sketch):
- Wood flooring in living areas/bedrooms that are SHOWN
- Tile flooring in bathrooms/kitchens that are SHOWN
- Simple furniture icons ONLY in rooms that clearly exist
- Do NOT add a room just to put furniture in it

🚫 ABSOLUTELY FORBIDDEN:
- Adding rooms that don't exist in the sketch
- Adding closets, en-suites, or utility rooms not shown
- Adding doors/windows where none are indicated
- Changing the shape or size of any room
- Creating rooms to place furniture
- Any branding or watermarks

BEFORE GENERATING: Count the rooms in the sketch. Your output MUST match this count exactly.`
  },
  {
    id: '3d-color',
    name: '3D Color',
    description: 'Full color 3D top-down view.',
    credits: 15,
    icon: Box,
    color: 'amber',
    prompt: `TASK: Convert the hand-drawn floor plan sketch (IMAGE 1) into a 3D top-down rendered floor plan.
IMAGE 2 shows the 2D layout conventions to follow.

⚠️ EXTREME CAUTION REQUIRED - READ CAREFULLY ⚠️

THIS IS A TRACING TASK, NOT A DESIGN TASK.
You must reproduce EXACTLY what is drawn in the sketch - nothing more, nothing less.
DO NOT use your imagination. DO NOT add anything that is not clearly visible.

MANDATORY ACCURACY RULES:
1. COUNT THE ROOMS: Count every distinct room/space in the sketch. Your output MUST have the IDENTICAL number of rooms.
2. TRACE WALLS EXACTLY: Follow the wall lines precisely as drawn.
3. PRESERVE PROPORTIONS: Do not resize or rebalance rooms.
4. NO INVENTION: If you cannot clearly see something - DO NOT ADD IT.
5. NO EXTRA ROOMS: Do not add closets, storage, en-suites, or any space not clearly shown.

WHAT TO INCLUDE (only if clearly visible in sketch):
- Walls exactly as drawn (with 3D depth)
- Doors/openings where clearly indicated
- Windows where clearly marked

3D ELEMENTS (only in rooms that EXIST in the sketch):
- Realistic flooring textures
- 3D furniture ONLY in rooms that clearly exist
- Soft shadows for depth
- Do NOT add a room just to put furniture in it

🚫 ABSOLUTELY FORBIDDEN:
- Adding rooms that don't exist in the sketch
- Adding closets, en-suites, or utility rooms not shown
- Adding doors/windows where none are indicated
- Changing the shape or size of any room
- Creating rooms to place furniture
- Making it cartoonish
- Any branding or watermarks

BEFORE GENERATING: Count the rooms in the sketch. Your output MUST match this count exactly.`
  }
];

const Editor: React.FC<EditorProps> = ({ imageUrl, onProcess, isProcessing, onDownload, onOpenDashboard, styleReferenceDataUrl }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [rotation, setRotation] = useState(0);
  const [selectedStyle, setSelectedStyle] = useState(CONVERSION_STYLES[0].id);
  const [referenceImageData, setReferenceImageData] = useState<string | null>(null);

  // Load reference floor plan image. Prefer the user's uploaded brand style
  // reference; otherwise fall back to the built-in reference plan.
  useEffect(() => {
    if (styleReferenceDataUrl) {
      setReferenceImageData(styleReferenceDataUrl);
      console.log('Using user-uploaded floor plan style reference');
      return;
    }

    const loadReferenceImage = async () => {
      try {
        const response = await fetch(REFERENCE_FLOOR_PLAN_URL);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setReferenceImageData(reader.result as string);
          console.log('Reference floor plan image loaded');
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Failed to load reference floor plan image:', error);
      }
    };
    loadReferenceImage();
  }, [styleReferenceDataUrl]);
  
  // Initialize Canvas
  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    
    const calculateSize = () => {
      if (containerRef.current && img.width > 0) {
        const containerW = containerRef.current.clientWidth;
        const containerH = containerRef.current.clientHeight;
        
        // Account for rotation
        const isRotated = rotation === 90 || rotation === 270;
        const imgW = isRotated ? img.height : img.width;
        const imgH = isRotated ? img.width : img.height;
        
        const imgAspect = imgW / imgH;
        const containerAspect = containerW / containerH;
        
        let renderW, renderH;
        
        if (imgAspect > containerAspect) {
          renderW = containerW;
          renderH = containerW / imgAspect;
        } else {
          renderH = containerH;
          renderW = containerH * imgAspect;
        }
        
        setCanvasSize({ width: renderW, height: renderH });
      }
    };
    
    img.onload = () => {
      imageRef.current = img;
      calculateSize();
    };
    
    window.addEventListener('resize', calculateSize);
    return () => window.removeEventListener('resize', calculateSize);
  }, [imageUrl, rotation]);

  // Draw rotated image on canvas
  useEffect(() => {
    if (!canvasRef.current || !imageRef.current || canvasSize.width === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const img = imageRef.current;
    
    // Set canvas size
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    
    // Clear and draw rotated
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    
    // Calculate draw dimensions based on rotation
    const isRotated = rotation === 90 || rotation === 270;
    const drawW = isRotated ? canvas.height : canvas.width;
    const drawH = isRotated ? canvas.width : canvas.height;
    
    ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    ctx.restore();
  }, [canvasSize, rotation]);

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90 + 360) % 360);
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleConvert = () => {
    if (!canvasRef.current || isProcessing) return;
    
    const style = CONVERSION_STYLES.find(s => s.id === selectedStyle);
    if (!style) return;
    
    // Get the rotated image data
    const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.95);
    
    // Determine aspect ratio
    const aspectRatio = canvasSize.width > canvasSize.height ? '4:3' : '3:4';
    
    // Pass reference image if available
    onProcess(dataUrl, style.prompt, aspectRatio, null, false, style.credits, referenceImageData || undefined);
  };

  const selectedStyleData = CONVERSION_STYLES.find(s => s.id === selectedStyle);

  return (
    <div className="flex flex-col h-full bg-black">
      {/* Top Toolbar */}
      <div className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 shrink-0 shadow-sm z-10">
        <div className="flex items-center gap-4">
          {/* Rotation Controls */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider mr-2">Rotate</span>
            <button
              onClick={handleRotateLeft}
              disabled={isProcessing}
              className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Rotate Left 90°"
            >
              <RotateCcw size={20} />
            </button>
            <button
              onClick={handleRotateRight}
              disabled={isProcessing}
              className="p-2.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Rotate Right 90°"
            >
              <RotateCw size={20} />
            </button>
            {rotation !== 0 && (
              <span className="text-xs text-slate-500 ml-2">{rotation}°</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onOpenDashboard}
            className="p-2 text-slate-400 hover:text-teal-400 transition-colors"
            title="Dashboard"
          >
            <LayoutDashboard size={20} />
          </button>
          
          <button
            onClick={onDownload}
            className="p-2 text-slate-400 hover:text-teal-400 transition-colors"
            title="Download Original"
          >
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* Main Workspace */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 bg-black flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
        >
          {/* Blueprint grid pattern */}
          <div className="absolute inset-0 opacity-10" 
               style={{
                 backgroundImage: 'linear-gradient(#0d9488 1px, transparent 1px), linear-gradient(90deg, #0d9488 1px, transparent 1px)',
                 backgroundSize: '20px 20px'
               }}></div>

          {canvasSize.width > 0 && (
            <div 
              style={{ width: canvasSize.width, height: canvasSize.height }}
              className="relative shadow-2xl shadow-black rounded-lg overflow-hidden bg-white ring-2 ring-teal-500/30"
            >
              <canvas
                ref={canvasRef}
                className="w-full h-full"
              />
              
              {/* Processing Overlay */}
              {isProcessing && (
                <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
                  <div className="bg-slate-900/95 p-6 rounded-2xl shadow-2xl border border-teal-500/30 flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="absolute inset-0 bg-teal-500/20 blur-xl rounded-full animate-pulse"></div>
                      <Loader2 className="w-12 h-12 text-teal-500 animate-spin relative z-10" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-slate-100 text-lg">Converting Sketch</p>
                      <p className="text-sm text-slate-400 mt-1">AI is generating your floor plan...</p>
                      <p className="text-xs text-teal-400 mt-2">This may take 20-30 seconds</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Conversion Style */}
        <div className="hidden md:flex w-80 bg-slate-900 border-l border-slate-800 flex-col shrink-0 shadow-xl z-20">
          <div className="p-6 border-b border-slate-800">
            <h3 className="font-semibold text-teal-300 mb-2 text-lg">Conversion Style</h3>
            <p className="text-xs text-slate-500">Select the output format for your floor plan</p>
          </div>
          
          <div className="p-4 space-y-3 flex-1 overflow-y-auto">
            {CONVERSION_STYLES.map((style) => {
              const Icon = style.icon;
              const isSelected = selectedStyle === style.id;
              const colorClasses: Record<string, { bg: string; border: string; text: string; icon: string }> = {
                teal: { bg: 'bg-teal-900/30', border: 'border-teal-500', text: 'text-teal-200', icon: 'text-teal-400' },
                purple: { bg: 'bg-purple-900/30', border: 'border-purple-500', text: 'text-purple-200', icon: 'text-purple-400' },
                amber: { bg: 'bg-amber-900/30', border: 'border-amber-500', text: 'text-amber-200', icon: 'text-amber-400' },
              };
              const colors = colorClasses[style.color];
              
              return (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  disabled={isProcessing}
                  className={`
                    w-full p-4 rounded-xl text-left transition-all border-2
                    ${isSelected 
                      ? `${colors.bg} ${colors.border} shadow-lg` 
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? colors.bg : 'bg-slate-700'}`}>
                      <Icon size={20} className={isSelected ? colors.icon : 'text-slate-400'} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold ${isSelected ? colors.text : 'text-slate-200'}`}>
                          {style.name}
                        </span>
                        <div className={`flex items-center gap-1 text-xs ${isSelected ? colors.icon : 'text-slate-500'}`}>
                          <Coins size={12} />
                          <span>{style.credits}</span>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                        {style.description}
                      </p>
                    </div>
                  </div>
                  {isSelected && (
                    <div className={`mt-3 pt-3 border-t ${colors.border}/30`}>
                      <div className="flex items-center gap-2 text-xs text-slate-400">
                        <div className={`w-2 h-2 rounded-full ${colors.icon.replace('text-', 'bg-')}`}></div>
                        Selected
                      </div>
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Convert Button */}
          <div className="p-4 border-t border-slate-800 bg-slate-900/50 mt-auto">
            <button
              onClick={handleConvert}
              disabled={isProcessing}
              className={`
                w-full py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all
                ${isProcessing 
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed' 
                  : 'bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white shadow-lg shadow-teal-900/30 hover:shadow-teal-900/50 active:scale-[0.98]'
                }
              `}
            >
              {isProcessing ? (
                <>
                  <Loader2 size={22} className="animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  Convert Sketch
                  <span className="text-sm opacity-80">({selectedStyleData?.credits} credits)</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Bottom Bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 p-4 z-30 pb-safe">
          {/* Style Selection */}
          <div className="flex items-center gap-2 mb-3">
            {CONVERSION_STYLES.map((style) => {
              const Icon = style.icon;
              const isSelected = selectedStyle === style.id;
              return (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`flex-1 p-2.5 rounded-lg flex flex-col items-center gap-1 transition-all border
                    ${isSelected 
                      ? 'bg-teal-900/30 border-teal-500 text-teal-300' 
                      : 'bg-slate-800 border-slate-700 text-slate-400'
                    }
                  `}
                >
                  <Icon size={16} />
                  <span className="text-[9px] font-medium">{style.name}</span>
                </button>
              );
            })}
          </div>
          
          <button
            onClick={handleConvert}
            disabled={isProcessing}
            className={`
              w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all
              ${isProcessing 
                ? 'bg-slate-700 text-slate-400' 
                : 'bg-teal-600 hover:bg-teal-500 text-white shadow-lg'
              }
            `}
          >
            {isProcessing ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Converting...
              </>
            ) : (
              <>
                Convert Sketch ({selectedStyleData?.credits} credits)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Editor;
